/**
 * Errors raised by the OAuth token endpoint, classified by recoverability.
 *
 * GH #1538: a `400 invalid_grant` from the Cognito token endpoint means the
 * stored refresh token is expired/revoked — retrying can never succeed, so the
 * only recovery is to re-authenticate. Everything else (network blips, 5xx,
 * throttling) is transient and must NOT log the user out — that would discard a
 * still-valid session (the GH #1469 regression).
 *
 * W2: a `401` from the auth-proxy refresh endpoint means the HttpOnly cookie is
 * absent, so there is no credential left to retry with — also permanent. This
 * matters because the refresh Lambda CLEARS the cookie on `invalid_grant`: the
 * first failure is a permanent 400, but every call after it is a 401. Treating
 * that follow-up as transient stranded users on a localStorage session that can
 * never refresh, because AuthGuard deliberately does not re-authenticate on a
 * transient error (GH #1469). Post-W2 a stored session no longer implies a
 * stored refresh token — only the cookie does.
 */
export class AuthTokenError extends Error {
  readonly status: number
  readonly oauthError?: string
  /** `true` when retrying cannot succeed and the user must re-authenticate. */
  readonly permanent: boolean

  constructor(status: number, oauthError?: string) {
    super(`Authentication failed: ${oauthError ?? status}`)
    this.name = 'AuthTokenError'
    this.status = status
    this.oauthError = oauthError
    this.permanent = oauthError === 'invalid_grant' || status === 401
  }
}

/** `true` when an error from the token endpoint cannot be recovered by retrying. */
export function isPermanentAuthError(error: unknown): error is AuthTokenError {
  return error instanceof AuthTokenError && error.permanent
}
